import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Home as HomeIcon, Wrench, CheckCircle2, XCircle, ShieldCheck, Crown, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, dashFor } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo, Field, Loader } from "../components/kit";
import { BrandLogo } from "../components/Layout";
import { SignupPaymentChoice } from "../components/PaymentMode";
import { fmtDate } from "../lib/api";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-[80vh] grid place-items-center bg-[#FAF9F6] grain px-4 py-12">
    <div className="w-full max-w-md card p-8 fade-up">
      <div className="mb-6"><BrandLogo className="h-7 mb-6" /><h1 className="text-2xl font-bold">{title}</h1>{sub && <p className="muted text-sm mt-1">{sub}</p>}</div>
      {children}
    </div>
  </div>
);

export function Login() {
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  useSeo("Log in");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      const u = await refresh();
      nav(sp.get("next") || dashFor(u || data));
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Log in to MyHomelyHub">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email or username"><input className="input" autoComplete="username" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="login-identifier" required /></Field>
        <Field label="Password"><input type="password" autoComplete="current-password" className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" required /></Field>
        <button className="btn-coral w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Logging in…" : "Log In"}</button>
      </form>
      <div className="flex justify-between text-sm mt-5"><Link to="/forgot-password" className="text-slate-500 hover:text-[#FF8166]" data-testid="forgot-link">Forgot password?</Link><Link to="/register" className="font-semibold text-[#FF8166]" data-testid="register-link">Create account</Link></div>
    </Shell>
  );
}

export function Register() {
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") || "");
  const [f, setF] = useState({ name: "", email: "", username: "", password: "", accept_terms: false, marketing_consent: false, referral_code: sp.get("ref") || "", payment_mode: "platform" });
  const [busy, setBusy] = useState(false);
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  useSeo("Create an account");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, role });
      setUser(data);
      await refresh();
      toast.success("Welcome to MyHomelyHub!");
      nav(role === "provider" ? "/provider/onboarding" : "/dashboard");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  if (!role) return (
    <Shell title="Join MyHomelyHub" sub="How will you use MyHomelyHub?">
      <div className="grid gap-3">
        {[["homeowner", "I NEED A PROFESSIONAL", "Find help or post a job for free", HomeIcon], ["provider", "I AM A PROFESSIONAL", "Build your profile and find local work", Wrench]].map(([r, t, s, I]) => (
          <button key={r} onClick={() => setRole(r)} className="card-hover p-5 text-left flex gap-4 items-center" data-testid={`register-role-${r}`}>
            <span className="w-12 h-12 rounded-xl bg-[#FFF1EE] text-[#FF8166] grid place-items-center"><I /></span>
            <span><span className="block font-bold text-slate-800 text-sm tracking-wide">{t}</span><span className="text-sm muted">{s}</span></span>
          </button>))}
      </div>
      <p className="text-sm text-center mt-6">Already a member? <Link to="/login" className="font-semibold text-[#FF8166]">Log in</Link></p>
    </Shell>
  );
  return (
    <Shell title={role === "provider" ? "Create your professional account" : "Create your account"} sub={<button onClick={() => setRole("")} className="underline">Change account type</button>}>
      <form onSubmit={submit} className="space-y-3">
        <Field label={role === "provider" ? "Your name" : "Full name"}><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="register-name" /></Field>
        <Field label="Email"><input type="email" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="register-email" /></Field>
        <Field label="Username (optional)"><input className="input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="register-username" /></Field>
        <Field label="Password" hint="At least 8 characters"><input type="password" className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} data-testid="register-password" /></Field>
        {role === "provider" && <SignupPaymentChoice value={f.payment_mode} onChange={(v) => setF({ ...f, payment_mode: v })} />}
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.accept_terms} onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} data-testid="register-terms" />
          <span>I confirm I have read and agree to the <Link className="underline font-medium" target="_blank" to={role === "provider" ? "/legal/service-provider-terms" : "/legal/terms"} data-testid="register-terms-link">{role === "provider" ? "Service Provider Terms & Conditions" : "General Terms & Conditions"}</Link> and <Link className="underline" target="_blank" to="/legal/privacy-policy" data-testid="register-privacy-link">Privacy Policy</Link></span></label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} data-testid="register-marketing" />Send me tips and offers (optional)</label>
        <button className="btn-coral w-full" disabled={busy || !f.accept_terms} data-testid="register-submit-btn">{busy ? "Creating…" : "Create account"}</button>
      </form>
    </Shell>
  );
}

const InviteOffer = ({ inv }) => (
  <ul className="rounded-xl bg-[#FFF1EE] p-4 text-sm space-y-2 mb-5" data-testid="invite-offer">
    <li className="flex gap-2"><Crown size={16} className="text-[#FF8166] shrink-0" /><span><b>{inv.months} months of {inv.plan_name}</b> free</span></li>
    <li className="flex gap-2"><ShieldCheck size={16} className="text-sky-600 shrink-0" /><span>Approved straight away and shown as fully verified{inv.source ? `, because you're already vetted through ${inv.source}` : ""}</span></li>
    <li className="flex gap-2"><Clock size={16} className="text-slate-500 shrink-0" /><span>This invite expires on {fmtDate(inv.expires_at)}</span></li>
  </ul>
);

export function AcceptInvite() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: "", business_name: "", password: "", accept_terms: false, marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  useSeo("Accept your invite");
  useEffect(() => { api.get(`/invites/${token}`).then(({ data }) => { setInv(data); setF((x) => ({ ...x, name: data.contact_name || "", business_name: data.company_name || "" })); }).catch((e) => setErr(errMsg(e))); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, f);
      setUser(data); await refresh();
      toast.success(`Welcome! ${inv.plan_name} is now active.`);
      nav("/provider/onboarding");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  if (err) return <Shell title="Invite unavailable"><p className="text-sm text-rose-700" data-testid="invite-error">{err}</p><Link to="/login" className="btn-navy w-full mt-6">Go to log in</Link></Shell>;
  if (!inv) return <Shell title="Checking your invite"><Loader /></Shell>;
  return (
    <Shell title={`${inv.company_name || "You"}, you're invited`} sub="Join MyHomelyHub as a verified professional.">
      <InviteOffer inv={inv} />
      <form onSubmit={submit} className="space-y-3">
        <Field label="Your name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="invite-accept-name" /></Field>
        <Field label="Business name"><input className="input" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} required data-testid="invite-accept-business" /></Field>
        <Field label="Email"><input className="input bg-slate-50" value={inv.email} readOnly data-testid="invite-accept-email" /></Field>
        <Field label="Choose a password" hint="At least 8 characters"><input type="password" autoComplete="new-password" className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} data-testid="invite-accept-password" /></Field>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.accept_terms} onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} data-testid="invite-accept-terms" />
          <span>I confirm I have read and agree to the <Link className="underline font-medium" target="_blank" to="/legal/service-provider-terms" data-testid="invite-terms-link">Service Provider Terms & Conditions</Link> and <Link className="underline" target="_blank" to="/legal/privacy-policy" data-testid="invite-privacy-link">Privacy Policy</Link></span></label>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} data-testid="invite-accept-marketing" />Send me tips and offers (optional)</label>
        <button className="btn-coral w-full" disabled={busy || !f.accept_terms} data-testid="invite-accept-submit">{busy ? "Creating your account…" : "Accept invite & create account"}</button>
      </form>
      <p className="text-sm text-center mt-5">Already a member? <Link to="/login" className="font-semibold text-[#FF8166]">Log in</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <Shell title="Reset your password" sub="We'll email you a reset link.">
      {sent ? <p className="text-sm text-emerald-700" data-testid="forgot-sent">If that email exists, a reset link has been sent.</p> : (
        <form onSubmit={async (e) => { e.preventDefault(); await api.post("/auth/forgot", { email }); setSent(true); }} className="space-y-4">
          <Field label="Email"><input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></Field>
          <button className="btn-coral w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>)}
    </Shell>
  );
}

export function Reset() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const nav = useNavigate();
  return (
    <Shell title="Choose a new password">
      <form onSubmit={async (e) => { e.preventDefault(); try { await api.post("/auth/reset", { token: sp.get("token"), password: pw }); toast.success("Password updated"); nav("/login"); } catch (er) { toast.error(errMsg(er)); } }} className="space-y-4">
        <Field label="New password"><input type="password" className="input" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password" /></Field>
        <button className="btn-coral w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </Shell>
  );
}

export function SecureAccount() {
  const [sp] = useSearchParams();
  const [st, setSt] = useState("ready");
  const { setUser } = useAuth();
  const go = async () => {
    setSt("busy");
    try {
      await api.post("/auth/secure-account", { token: sp.get("token") });
      await api.post("/auth/logout").catch(() => {});
      setUser(false);
      setSt("ok");
    } catch (er) { toast.error(errMsg(er)); setSt("fail"); }
  };
  return (
    <Shell title="Secure your account">
      {st === "ok" ? (
        <p className="flex gap-2 text-emerald-700" data-testid="secure-account-done"><CheckCircle2 className="shrink-0" />Done. We've signed you out on every device and emailed you a link to choose a new password.</p>
      ) : st === "fail" ? (
        <div className="flex gap-2 text-rose-700" data-testid="secure-account-failed"><XCircle className="shrink-0" /><span>This link is invalid, expired or already used. You can still <Link to="/forgot-password" className="underline">reset your password</Link>.</span></div>
      ) : (
        <div className="space-y-4">
          <p className="text-slate-600 text-sm">If you didn't sign in or change your password, we'll sign you out on every device and email you a link to choose a new password.</p>
          <button onClick={go} disabled={st === "busy"} className="btn-coral w-full flex items-center justify-center gap-2" data-testid="secure-account-btn"><ShieldCheck className="w-4 h-4" />{st === "busy" ? "Securing..." : "Sign me out everywhere"}</button>
        </div>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [st, setSt] = useState("loading");
  useEffect(() => { api.post("/auth/verify-email", { token: sp.get("token") }).then(() => setSt("ok")).catch(() => setSt("fail")); }, [sp]);
  return <Shell title="Email verification">{st === "loading" ? <Loader /> : st === "ok" ? <p className="flex gap-2 text-emerald-700"><CheckCircle2 />Your email is verified.</p> : <p className="flex gap-2 text-rose-700"><XCircle />This link is invalid or already used.</p>}</Shell>;
}

export function PaymentResult({ cancelled }) {
  const [sp] = useSearchParams();
  const [st, setSt] = useState(cancelled ? "cancelled" : "checking");
  const [kind, setKind] = useState("");
  const [jobId, setJobId] = useState(null);
  const { refresh, user } = useAuth();
  useEffect(() => {
    if (cancelled) return;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sp.get("session_id")}`);
        setKind(data.kind);
        setJobId(data.job_id && !["job_payment"].includes(data.kind) ? data.job_id : null);
        if (data.payment_status === "paid") { setSt("paid"); refresh(); return; }
        if (data.status === "expired" || data.payment_status === "failed") return setSt("failed");
      } catch { return setSt("failed"); }
      if (++n < 10) setTimeout(poll, 2000); else setSt("pending");
    };
    poll();
  }, []); // eslint-disable-line
  const msg = { checking: "Confirming your payment…", paid: "Payment successful — thank you!", failed: "Payment failed or expired.", pending: "Payment is still processing. We'll update your account shortly.", cancelled: "Payment cancelled. You have not been charged." }[st];
  return <Shell title="Payment"><p className="text-sm" data-testid="payment-status">{msg}</p><Link to={kind === "advert_pin" ? "/provider/adverts" : jobId ? `/dashboard/jobs/${jobId}` : dashFor(user)} className="btn-navy w-full mt-6" data-testid="payment-back-btn">{kind === "advert_pin" ? "Back to my adverts" : jobId ? "View my job" : "Back to dashboard"}</Link></Shell>;
}
